/** Sheets, toasts, lists, timelines and before-and-after (docs/52 §4.5, D8: no modal on a modal). */
import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useId, useRef, useState } from 'preact/hooks';
import { Icon, type IconName } from '../icons.tsx';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: ComponentChildren;
  closeLabel: string;
  children: ComponentChildren;
  footer?: ComponentChildren;
  /** Short description read with the title. */
  description?: string;
}

/**
 * Bottom sheet on phones, side panel from 768 px. Built on <dialog>.showModal(), so focus is trapped, the page
 * behind is inert, Escape closes it and focus returns to the opener. A Close button and a downward swipe on the
 * handle also close it (a button alternative exists for the gesture, WCAG 2.5.7).
 */
export function Sheet(p: SheetProps): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId().replace(/[^a-zA-Z0-9]/g, '');
  const drag = useRef<{ y: number; dy: number } | null>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (p.open && !d.open) {
      d.showModal();
      d.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    } else if (!p.open && d.open) d.close();
  }, [p.open]);
  useEffect(() => () => ref.current?.open && ref.current.close(), []);
  return (
    <dialog
      ref={ref}
      class="sheet"
      aria-labelledby={`s${titleId}`}
      aria-description={p.description}
      onClose={() => p.open && p.onClose()}
      onCancel={(e) => {
        e.preventDefault();
        p.onClose();
      }}
      onClick={(e) => {
        // A tap on the backdrop (the dialog element itself, outside the panel box) closes it.
        if (e.target !== ref.current) return;
        const r = (e.target as HTMLDialogElement).getBoundingClientRect();
        const { clientX: x, clientY: y } = e as unknown as MouseEvent;
        if (x < r.left || x > r.right || y < r.top || y > r.bottom) p.onClose();
      }}
    >
      <div
        class="sheet-grab"
        aria-hidden="true"
        onTouchStart={(e) => {
          drag.current = { y: e.touches[0]!.clientY, dy: 0 };
        }}
        onTouchMove={(e) => {
          if (!drag.current || !ref.current) return;
          drag.current.dy = Math.max(0, e.touches[0]!.clientY - drag.current.y);
          ref.current.style.transform = `translateY(${drag.current.dy}px)`;
        }}
        onTouchEnd={() => {
          if (!ref.current) return;
          const dy = drag.current?.dy ?? 0;
          ref.current.style.transform = '';
          drag.current = null;
          if (dy > 80) p.onClose();
        }}
      />
      <div class="sheet-head">
        <h2 id={`s${titleId}`}>{p.title}</h2>
        <button type="button" class="icon-btn" onClick={p.onClose}>
          <Icon name="x" />
          <span>{p.closeLabel}</span>
        </button>
      </div>
      <div class="sheet-body">{p.open ? p.children : null}</div>
      {p.footer && p.open && <div class="sheet-foot">{p.footer}</div>}
    </dialog>
  );
}

export interface ToastItem {
  id: number;
  text: string;
  actionLabel?: string;
  onAction?: () => void;
}

/** Bottom, 4 seconds, Undo where undo exists, announced politely (docs/52 §4.5). */
export function ToastRegion(p: { toasts: ToastItem[]; onDismiss: (id: number) => void }): JSX.Element {
  return (
    <div class="toasts" role="status" aria-live="polite" aria-atomic="false">
      {p.toasts.map((t) => (
        <div class="toast" key={t.id}>
          <p>{t.text}</p>
          {t.actionLabel && (
            <button
              type="button"
              class="btn"
              data-variant="quiet"
              onClick={() => {
                t.onAction?.();
                p.onDismiss(t.id);
              }}
            >
              {t.actionLabel}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export interface RowProps {
  href?: string;
  onClick?: () => void;
  title: ComponentChildren;
  meta?: ComponentChildren;
  time?: ComponentChildren;
  chip?: ComponentChildren;
  unread?: boolean;
  /** Screen reader prefix for unread rows ("New"). Unread is never colour alone: the row is bold and says so. */
  unreadLabel?: string;
  lead?: ComponentChildren;
  current?: boolean;
  chevron?: boolean;
  external?: boolean;
}

/** List row: 64 px minimum, title, one metadata line, status chip, chevron (docs/52 §4.5). */
export function Row(p: RowProps): JSX.Element {
  const body = (
    <>
      <span class="row-lead">{p.unread ? <span class="unread-dot" aria-hidden="true" /> : p.lead}</span>
      <span class="row-main">
        <span class="row-title">
          {p.unread && p.unreadLabel && <span class="sr-only">{p.unreadLabel}: </span>}
          {p.title}
        </span>
        {p.meta && <span class="row-meta" style="display:block">{p.meta}</span>}
      </span>
      <span class="row-end">
        {(p.time || p.chip) && (
          <span class="row-aside">
            {p.time && <span class="row-time">{p.time}</span>}
            {p.chip}
          </span>
        )}
        {p.chevron !== false && (p.href || p.onClick) && <Icon name={p.external ? 'external' : 'chevron-right'} class="row-chev" size={20} />}
      </span>
    </>
  );
  if (p.href)
    return (
      <a
        class="row"
        href={p.href}
        data-unread={p.unread ? '' : undefined}
        aria-current={p.current ? 'page' : undefined}
        target={p.external ? '_blank' : undefined}
        rel={p.external ? 'noopener noreferrer' : undefined}
      >
        {body}
      </a>
    );
  if (p.onClick)
    return (
      <button type="button" class="row" data-unread={p.unread ? '' : undefined} onClick={p.onClick}>
        {body}
      </button>
    );
  return (
    <div class="row" data-unread={p.unread ? '' : undefined}>
      {body}
    </div>
  );
}

export function List(p: { children: ComponentChildren; label?: string }): JSX.Element {
  return (
    <ul class="list" role="list" aria-label={p.label}>
      {p.children}
    </ul>
  );
}

export interface TimelineStep {
  key: string;
  label: ComponentChildren;
  time?: ComponentChildren;
  state: 'done' | 'current' | 'todo';
  detail?: ComponentChildren;
}

export function Timeline(p: { steps: TimelineStep[]; label: string; stateLabels: Record<TimelineStep['state'], string> }): JSX.Element {
  return (
    <ol class="timeline" role="list" aria-label={p.label}>
      {p.steps.map((s) => (
        <li key={s.key} data-state={s.state} aria-current={s.state === 'current' ? 'step' : undefined}>
          <span class="tl-dot" aria-hidden="true">
            {s.state === 'done' && <Icon name="check" size={16} />}
          </span>
          <div class="tl-body">
            <span class="tl-label">
              <span class="sr-only">{p.stateLabels[s.state]}: </span>
              {s.label}
            </span>
            {s.time && <div class="tl-time">{s.time}</div>}
            {s.detail}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Side by side from 768 px, stacked with a toggle on phones; never a drag slider (52-D6). */
export function BeforeAfter(p: { before: string; after: string; beforeLabel: string; afterLabel: string; toggleLabel: string; alt: (which: string) => string }): JSX.Element {
  const [show, setShow] = useState<'before' | 'after'>('after');
  const wide = typeof window !== 'undefined' && window.matchMedia?.('(min-width: 768px)').matches;
  if (wide)
    return (
      <div class="ba" data-mode="split">
        <figure>
          <figcaption>{p.beforeLabel}</figcaption>
          <img src={p.before} alt={p.alt(p.beforeLabel)} loading="lazy" decoding="async" />
        </figure>
        <figure>
          <figcaption>{p.afterLabel}</figcaption>
          <img src={p.after} alt={p.alt(p.afterLabel)} loading="lazy" decoding="async" />
        </figure>
      </div>
    );
  return (
    <div class="ba">
      <div class="segmented" role="group" aria-label={p.toggleLabel}>
        <button type="button" aria-pressed={show === 'before'} onClick={() => setShow('before')}>
          {p.beforeLabel}
        </button>
        <button type="button" aria-pressed={show === 'after'} onClick={() => setShow('after')}>
          {p.afterLabel}
        </button>
      </div>
      <figure>
        <img src={show === 'before' ? p.before : p.after} alt={p.alt(show === 'before' ? p.beforeLabel : p.afterLabel)} loading="lazy" decoding="async" />
      </figure>
    </div>
  );
}

export function IconLabel(p: { icon: IconName; children: ComponentChildren }): JSX.Element {
  return (
    <span class="cluster" style="gap:6px">
      <Icon name={p.icon} size={18} />
      <span>{p.children}</span>
    </span>
  );
}
